import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login, error, setError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user !== false && user !== null) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    await login(email.trim(), password);
    setSubmitting(false);
  };

  const presets = [
    { label: "Super Admin", email: "admin@dwellstone.io" },
    { label: "Team Admin", email: "teamadmin@dwellstone.io" },
    { label: "Scanner", email: "scanner@dwellstone.io" },
    { label: "Viewer", email: "viewer@dwellstone.io" },
  ];

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#F7F7F8]">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:col-span-2 bg-[#0A0A0A] text-white relative overflow-hidden">
        <div className="absolute inset-0 diag-stripes opacity-40" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white text-[#0A0A0A] flex items-center justify-center font-mono font-bold">
              D
            </div>
            <div className="leading-tight">
              <div className="font-bold tracking-tight">DWELLSTONE</div>
              <div className="ds-overline" style={{ color: "#8A8A93" }}>
                Document Vault
              </div>
            </div>
          </div>
          <div>
            <div className="ds-overline" style={{ color: "#8A8A93" }}>
              Secure // Audited // Indexed
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight mt-3">
              The system of record for every document your team touches.
            </h1>
            <p className="mt-4 text-[#A0A0A8] max-w-md leading-relaxed text-sm">
              Multi-team access control, OCR-driven classification, scan-to-email
              ingestion, and an audit trail you can hand to a regulator.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-px bg-[#222222] border border-[#222222]">
            {[
              { v: "100MB", k: "Per file" },
              { v: "OCR", k: "Auto-classified" },
              { v: "Audit", k: "Every action" },
            ].map((s) => (
              <div key={s.k} className="bg-[#0A0A0A] p-4">
                <div className="text-2xl font-display font-bold">{s.v}</div>
                <div className="ds-overline" style={{ color: "#8A8A93" }}>
                  {s.k}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="lg:col-span-3 flex items-center justify-center p-6">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md ds-card p-8"
          data-testid="login-form"
        >
          <div className="ds-overline mb-2">Sign in</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">
            Access the document vault
          </h2>
          <p className="text-[13px] text-[#4A4A52] mt-1">
            Use your assigned credentials. Sessions are audited.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="ds-label block mb-1">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                autoFocus
                className="ds-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@dwellstone.io"
              />
            </div>
            <div>
              <label className="ds-label block mb-1">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                className="ds-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div
                className="ds-badge"
                style={{ background: "#FEF3F2", color: "#B42318", borderColor: "#FECDCA", padding: "6px 10px" }}
                data-testid="login-error"
              >
                <Lock size={12} /> {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="login-submit-button"
              className="ds-btn-primary w-full flex items-center justify-center gap-2"
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in"} <ArrowRight size={14} />
            </button>
          </div>

          <div className="mt-7 pt-5 border-t border-[#E5E5E9]">
            <div className="ds-overline mb-2">Demo accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {presets.map((p) => (
                <button
                  key={p.email}
                  type="button"
                  data-testid={`preset-${p.label.replace(/\s/g, "-").toLowerCase()}`}
                  className="ds-btn-secondary text-left text-[12px] px-3 py-2"
                  onClick={() => {
                    setEmail(p.email);
                    setPassword("Admin@12345");
                  }}
                >
                  <div className="font-semibold">{p.label}</div>
                  <div className="text-[11px] text-[#8A8A93] truncate">{p.email}</div>
                </button>
              ))}
            </div>
            <div className="ds-overline mt-3" style={{ fontSize: 10 }}>
              Password for all demo accounts: <span className="font-mono">Admin@12345</span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
